import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  Alert,
  LinearProgress,
  Tooltip,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Fab,
  AppBar,
  Toolbar,
  Collapse,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import {
  ExpandMore as ExpandMoreIcon,
  NoteAdd as NoteAddIcon,
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  Visibility,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { apiWithRetry } from '../api';

// Convert file to base64 string
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// Compress image and convert to base64
const compressAndConvertImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 800;
        const maxHeight = 800;
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64
        const base64 = canvas.toDataURL('image/jpeg', 0.7);
        resolve(base64);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const TradeJournal = ({ initialEntries = [] }) => {
  const [entries, setEntries] = useState(Array.isArray(initialEntries) ? initialEntries : []);
  const [newText, setNewText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('general');
  const [previewModal, setPreviewModal] = useState({ open: false, src: null });
  const [showAddForm, setShowAddForm] = useState(false);
  const [noteModal, setNoteModal] = useState({ open: false, note: null });
  const [sortOrder, setSortOrder] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  const tradingTopics = [
    'general',
    'psychology',
    'risk-management',
    'technical-analysis',
    'fundamental-analysis',
    'trade-setup',
    'trade-review',
    'market-news',
    'learning',
    'goals',
    'reflection',
    'strategy',
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    return () => {
      // Clean up blob URLs
      imagePreviews.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviews]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const res = await apiWithRetry.get('/api/journal');
      if (Array.isArray(res.data)) {
        setEntries(res.data);
        console.log('Fetched entries:', res.data.length);
        
        // Debug: Check image data
        res.data.forEach(entry => {
          if (entry.images && entry.images.length > 0) {
            console.log(`Entry ${entry._id} has images:`, {
              count: entry.images.length,
              firstImageType: typeof entry.images[0],
              firstImageStart: entry.images[0]?.substring(0, 50)
            });
          }
        });
      } else {
        setError('Invalid data format from server.');
        setEntries([]);
      }
    } catch (err) {
      const message = err.response?.status === 401
        ? 'Authentication failed. Please log in again.'
        : 'Failed to load journal entries.';
      setError(message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Create preview URLs for selected files
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newText.trim() && selectedFiles.length === 0) {
      setError('Please add text or images.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let imageBase64Strings = [];
      
      // Convert images to base64
      if (selectedFiles.length > 0) {
        for (let file of selectedFiles) {
          const base64String = await compressAndConvertImage(file);
          imageBase64Strings.push(base64String);
        }
      }

      const entryData = {
        text: newText.trim(),
        images: imageBase64Strings, // Store as base64 strings in MongoDB
        topic: selectedTopic,
      };

      console.log('Submitting journal entry:', {
        textLength: entryData.text.length,
        imageCount: entryData.images.length,
        firstImageType: entryData.images[0] ? typeof entryData.images[0] : 'none',
        firstImageStart: entryData.images[0]?.substring(0, 50) || 'none'
      });

      const res = await apiWithRetry.post('/api/journal', entryData);
      if (res.data) {
        setEntries((prev) => [res.data, ...prev]);
        setNewText('');
        setSelectedFiles([]);
        
        // Clean up preview URLs
        imagePreviews.forEach(url => {
          if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
        setImagePreviews([]);
        setSelectedTopic('general');
        setShowAddForm(false);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const openNoteModal = (note) => {
    console.log('Opening note modal:', {
      noteId: note._id,
      imageCount: note.images?.length || 0,
      firstImage: note.images?.[0]?.substring(0, 50) || 'none'
    });
    setNoteModal({ open: true, note });
  };
  
  const closeNoteModal = () => setNoteModal({ open: false, note: null });
  
  const openPreviewModal = (src) => {
    console.log('Opening preview modal with src:', src?.substring(0, 50));
    setPreviewModal({ open: true, src });
  };

  const filteredAndSortedEntries = entries
    .filter((entry) => {
      const matchesTopic = selectedTopic === 'all' || entry.topic === selectedTopic;
      const matchesSearch =
        !searchQuery ||
        entry.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.topic?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTopic && matchesSearch;
    })
    .sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortOrder === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortOrder === 'topic') return (a.topic || 'general').localeCompare(b.topic || 'general');
      return 0;
    });

  const groupedEntries = filteredAndSortedEntries.reduce((acc, entry) => {
    const date = new Date(entry.createdAt).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <IconButton
            color="primary"
            onClick={() => window.history.back()}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Trading Journal
            </Typography>
            <Chip label={`${entries.length} entries`} color="primary" variant="outlined" size="small" />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort Order</InputLabel>
              <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} label="Sort Order">
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="topic">By Topic</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filter Topic</InputLabel>
              <Select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)} label="Filter Topic">
                <MenuItem value="all">All Topics</MenuItem>
                {tradingTopics.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic.charAt(0).toUpperCase() + topic.slice(1).replace('-', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search Notes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              sx={{ minWidth: 200 }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(!showAddForm)}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Add Entry
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>{entries.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Entries</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="secondary" sx={{ fontWeight: 600 }}>
                {entries.filter(e => e.topic === 'psychology').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Psychology Notes</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success" sx={{ fontWeight: 600 }}>
                {entries.filter(e => e.topic === 'trade-review').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Trade Reviews</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info" sx={{ fontWeight: 600 }}>
                {entries.filter(e => e.images && e.images.length > 0).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">With Images</Typography>
            </Card>
          </Grid>
        </Grid>

        <Collapse in={showAddForm} timeout={400}>
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600 }}>
              <NoteAddIcon /> New Trading Note
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Note Text"
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Write your thoughts..."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Topic</InputLabel>
                    <Select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)} label="Topic">
                      {tradingTopics.map((topic) => (
                        <MenuItem key={topic} value={topic}>
                          {topic.charAt(0).toUpperCase() + topic.slice(1).replace('-', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                  Add Screenshots
                  <input type="file" hidden multiple accept="image/*" onChange={handleFileChange} />
                </Button>
                {imagePreviews.length > 0 && <Chip label={`${imagePreviews.length} files`} color="primary" />}
              </Box>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {imagePreviews.map((src, index) => (
                    <Box key={index} sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: 2,
                      cursor: 'pointer'
                    }}>
                      <img
                        src={src}
                        alt={`Preview ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onClick={() => openPreviewModal(src)}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.9)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (src.startsWith('blob:')) {
                            URL.revokeObjectURL(src);
                          }
                          setImagePreviews(prev => prev.filter((_, i) => i !== index));
                          setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || (!newText.trim() && selectedFiles.length === 0)}
                  startIcon={<NoteAddIcon />}
                  sx={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : 'Save Note'}
                </Button>
                <Button variant="outlined" onClick={() => setShowAddForm(false)} startIcon={<CloseIcon />}>
                  Cancel
                </Button>
              </Box>
            </form>
          </Paper>
        </Collapse>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600 }}>
          <AssessmentIcon /> Journal History
        </Typography>

        {loading ? (
          <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
        ) : sortedDates.length === 0 ? (
          <Alert severity="info">No entries found.</Alert>
        ) : (
          sortedDates.map((date) => (
            <Accordion key={date} defaultExpanded sx={{ mb: 2, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>{date} ({groupedEntries[date].length} notes)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {groupedEntries[date].map((entry) => (
                    <ListItem
                      key={entry._id}
                      onClick={() => openNoteModal(entry)}
                      sx={{
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        mb: 2,
                        p: 3,
                        mx: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 4 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(entry.createdAt).toLocaleTimeString()}
                          </Typography>
                          <Chip label={entry.topic || 'general'} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary"><Visibility /></IconButton>
                        </Tooltip>
                      </Box>
                      {entry.text && (
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                          {entry.text.length > 200 ? `${entry.text.substring(0, 200)}...` : entry.text}
                        </Typography>
                      )}
                      {entry.images && entry.images.length > 0 && (
                        <Grid container spacing={1}>
                          {entry.images.slice(0, 3).map((image, idx) => (
                            <Grid item xs={6} sm={4} md={3} key={idx}>
                              <img
                                src={image} // Directly use base64 string from MongoDB
                                alt={`Journal image ${idx + 1}`}
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  borderRadius: 8,
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPreviewModal(image);
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setShowAddForm(!showAddForm)}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>

      {/* Note Modal */}
      <Dialog open={noteModal.open} onClose={closeNoteModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Note Details</Typography>
            <IconButton onClick={closeNoteModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {noteModal.note && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip label={noteModal.note.topic || 'general'} color="primary" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  {new Date(noteModal.note.createdAt).toLocaleString()}
                </Typography>
              </Box>
              {noteModal.note.text && (
                <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                  {noteModal.note.text}
                </Typography>
              )}
              {noteModal.note.images && noteModal.note.images.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Images</Typography>
                  <Grid container spacing={2}>
                    {noteModal.note.images.map((image, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <img
                          src={image} // Directly use base64 string from MongoDB
                          alt={`Journal image ${idx + 1}`}
                          style={{ width: '100%', height: 'auto', borderRadius: 8, cursor: 'pointer' }}
                          onClick={() => openPreviewModal(image)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={previewModal.open} onClose={() => setPreviewModal({ open: false, src: null })} maxWidth="lg" fullWidth>
        <DialogContent sx={{ textAlign: 'center', p: 0 }}>
          {previewModal.src && (
            <img
              src={previewModal.src}
              alt="Full preview"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModal({ open: false, src: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

TradeJournal.propTypes = {
  initialEntries: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      text: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
      topic: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
};

export default TradeJournal;
